import { useCallback, useEffect, useRef } from 'react';
import { getLatestToken } from 'services/auth';
import { io, Socket } from 'socket.io-client';

export enum ESocketEvents {
  PRICE_RATING_UPDATED = 'price-rating-updated',
  SUBSCRIBE = 'subscribe',
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  ON_DATA_CHANGE = 'on-data-change',
}

interface SocketEvent<T = unknown> {
  event: ESocketEvents;
  onMessage: (message: T) => Promise<void> | void;
}

interface UseSocketIOOptions<T = unknown> {
  url: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
  requireAuth?: boolean;
  registerEvents: SocketEvent<T>[];
}

const useSocketIO = <T = unknown,>(options: UseSocketIOOptions<T>) => {
  const {
    url,
    onConnect,
    onDisconnect,
    requireAuth = true,
    registerEvents,
  } = options;

  const socketRef = useRef<Socket | null>(null);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  const sendMessage = useCallback(<D = unknown,>(event: string, data?: D) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit(event, data);
    }
  }, []);

  const connect = useCallback(async () => {
    if (!socketRef.current) {
      let extraHeaders = {};

      if (requireAuth) {
        const token = await getLatestToken();
        if (!token) {
          return;
        }
        extraHeaders = {
          Authorization: `Bearer ${token.accessToken}`,
        };
      }

      const socket = io(url, {
        autoConnect: false,
        extraHeaders,
      });

      socket.on(ESocketEvents.CONNECT, () => {
        if (onConnect) onConnect();
      });

      socket.on(ESocketEvents.DISCONNECT, () => {
        if (onDisconnect) onDisconnect();
      });

      registerEvents.forEach(({ event, onMessage }) => {
        socket.on(event, async (data: T) => {
          if (onMessage) {
            await onMessage(data);
          }
        });
      });

      socketRef.current = socket;
      socket.connect();
    }
  }, [url, onConnect, onDisconnect, requireAuth, registerEvents]);

  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isConnected: socketRef.current?.connected || false,
    sendMessage,
    connect,
    disconnect,
  };
};

export default useSocketIO;
